import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api";
import type { FeatureAccess } from "../features";
import { occupations, purposes, type SearchSettings } from "../reference-data";
import { ProfileExtras } from "./ProfileExtras";
import { ChoiceList, MenuRow, PageDialog, RangePair } from "./ReferenceUI";

const defaults: SearchSettings = {
  interestedIn: ["female", "male", "other"],
  ageMin: 18,
  ageMax: 99,
  purpose: null,
  occupation: null,
  heightMin: 140,
  heightMax: 210,
  station: null,
  socialOnly: false,
  videoOnly: false,
  autoExpandAge: false,
};
export function DiscoveryFilters({ onClose, onApplied }: { onClose(): void; onApplied(): void }) {
  const [premium, setPremium] = useState(false);
  const [videoAccess, setVideoAccess] = useState(false);
  const [value, setValue] = useState<SearchSettings | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<"purpose" | "occupation" | null>(null);
  const load = useCallback(async () => {
    setError("");
    try {
      const [settings, rights] = await Promise.all([
        api<SearchSettings>("/v1/me/discovery-settings"),
        api<FeatureAccess>("/v1/me/features").catch(() => null),
      ]);
      setValue(settings);
      setVideoAccess(rights?.features.video ?? false);
    } catch {
      setError("Не удалось загрузить фильтры.");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const save = async () => {
    if (!value || busy) return;
    setBusy(true);
    setError("");
    try {
      await api("/v1/me/discovery-settings", { method: "PUT", body: JSON.stringify(value) });
      onApplied();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === "premium_required") setPremium(true);
      setError("Не удалось сохранить фильтры. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  };
  if (picker && value)
    return (
      <ChoiceList
        title={picker === "purpose" ? "Я хочу" : "Деятельность"}
        options={picker === "purpose" ? [...purposes] : occupations.map((o) => ({ value: o, label: o }))}
        value={value[picker]}
        searchable={picker === "occupation"}
        onClose={() => setPicker(null)}
        onSelect={(next) => {
          setValue({ ...value, [picker]: next });
          setPicker(null);
        }}
      />
    );
  return (
    <PageDialog
      title="Фильтры"
      onClose={onClose}
      busy={busy}
      action={
        <button type="button" disabled={busy || !value} onClick={() => setValue({ ...defaults })}>
          Сброс.
        </button>
      }
    >
      {!value ? (
        <div role="status">
          {error || "Загружаем фильтры…"}
          {error && (
            <button type="button" onClick={() => void load()}>
              Повторить
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="reference-caption">ВОЗРАСТ</p>
          <div className="reference-group">
            <RangePair
              title="Диапазон возраста"
              min={18}
              max={120}
              low={value.ageMin}
              high={value.ageMax}
              onChange={(ageMin, ageMax) => setValue({ ...value, ageMin, ageMax })}
            />
          </div>
          <label className="reference-row">
            <span className="reference-row-label">Расширить автоматически</span>
            <input
              type="checkbox"
              className="reference-switch"
              checked={value.autoExpandAge}
              onChange={(e) => setValue({ ...value, autoExpandAge: e.target.checked })}
            />
          </label>
          <p className="reference-note">
            Если подходящие анкеты закончились, покажем людей другого возраста. Остальные фильтры сохраняются.
          </p>
          <p className="reference-caption">КОГО ПОКАЗЫВАТЬ</p>
          <div className="reference-group">
            {(
              [
                ["female", "Женщин"],
                ["male", "Мужчин"],
                ["other", "Других"],
              ] as const
            ).map(([key, label]) => (
              <label className="reference-row" key={key}>
                <span className="reference-row-label">{label}</span>
                <input
                  type="checkbox"
                  className="reference-switch"
                  checked={value.interestedIn.includes(key)}
                  onChange={(e) =>
                    setValue({
                      ...value,
                      interestedIn: e.target.checked
                        ? [...value.interestedIn, key]
                        : value.interestedIn.filter((v) => v !== key),
                    })
                  }
                />
              </label>
            ))}
          </div>
          <p className="reference-caption">МЕСТОПОЛОЖЕНИЕ</p>
          <label className="reference-field">
            Станция или район
            <input
              className="reference-input"
              placeholder="Не имеет значения"
              maxLength={80}
              value={value.station ?? ""}
              onChange={(e) => setValue({ ...value, station: e.target.value.trim() || null })}
            />
          </label>
          <p className="reference-caption">ДОПОЛНИТЕЛЬНО</p>
          <div className="reference-group">
            <MenuRow
              icon="♡"
              label="Я хочу"
              value={purposes.find((p) => p.value === value.purpose)?.label ?? "Указать"}
              onClick={() => setPicker("purpose")}
            />
            <MenuRow
              icon="▣"
              label="Деятельность"
              value={value.occupation ?? "Указать"}
              onClick={() => setPicker("occupation")}
            />
            <label className="reference-row">
              <span className="reference-row-label">С IG аккаунтом</span>
              <input
                type="checkbox"
                className="reference-switch"
                checked={value.socialOnly}
                onChange={(e) => setValue({ ...value, socialOnly: e.target.checked })}
              />
            </label>
          </div>
          <div className="reference-group">
            <label className="reference-row">
              <span className="reference-row-label">Только с видео{!videoAccess ? " · Premium" : ""}</span>
              <input
                type="checkbox"
                className="reference-switch"
                checked={value.videoOnly ?? false}
                onChange={(e) => {
                  if (e.target.checked && !videoAccess) setPremium(true);
                  else setValue({ ...value, videoOnly: e.target.checked });
                }}
              />
            </label>
          </div>
          <p className="reference-caption">РОСТ</p>
          <div className="reference-group">
            <RangePair
              title="Диапазон роста"
              min={140}
              max={210}
              low={value.heightMin}
              high={value.heightMax}
              unit=" см"
              onChange={(heightMin, heightMax) => setValue({ ...value, heightMin, heightMax })}
            />
          </div>
          {error && (
            <p className="reference-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="button"
            className="reference-primary reference-save"
            disabled={busy || !value.interestedIn.length}
            onClick={() => void save()}
          >
            {busy ? "Сохраняем…" : "Сохранить"}
          </button>
        </>
      )}
      {premium && <ProfileExtras page="premium" onClose={() => setPremium(false)} />}
    </PageDialog>
  );
}
